/**
 * HelpContextualArticles — articles rattachés à la page courante (état Contextuel,
 * champ vide), avec paliers de densité (HELP-DRAWER §6.2) :
 *   2–5  → liste plate ; 6–9 → liste compacte ; ≥10 → sections repliables par
 *   catégorie (celle du contexte ouverte, les autres repliées).
 *
 * Le regroupement ne s'applique QU'ICI (jamais en état Recherche).
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight } from 'lucide-react'

import { cn } from '../../lib/utils'
import { type HelpArticle } from '../../hooks/useHelp'
import { COMPACT_LIST_MIN, GROUPING_THRESHOLD, MIN_GROUP_SIZE } from '../../lib/helpConstants'
import { helpCategoryIcon } from '../../lib/helpCategories'

const UNCATEGORIZED = 'autres'

interface CategoryGroup {
    category: string
    articles: HelpArticle[]
}

/** Regroupe par catégorie ; les catégories < MIN_GROUP_SIZE retombent dans « autres ». */
function buildGroups(articles: HelpArticle[]): CategoryGroup[] {
    const byCat = new Map<string, HelpArticle[]>()
    for (const a of articles) {
        const cat = a.category || UNCATEGORIZED
        const arr = byCat.get(cat)
        if (arr) arr.push(a)
        else byCat.set(cat, [a])
    }
    const groups: CategoryGroup[] = []
    const leftovers: HelpArticle[] = []
    for (const [category, arr] of byCat) {
        if (category !== UNCATEGORIZED && arr.length >= MIN_GROUP_SIZE) {
            groups.push({ category, articles: arr })
        } else {
            leftovers.push(...arr)
        }
    }
    // Tri par taille décroissante pour des sections lisibles.
    groups.sort((a, b) => b.articles.length - a.articles.length)
    if (leftovers.length > 0) groups.push({ category: UNCATEGORIZED, articles: leftovers })
    return groups
}

function ArticleRow({
    article,
    compact,
    onSelect,
}: {
    article: HelpArticle
    compact?: boolean
    onSelect: (slug: string) => void
}) {
    return (
        <li>
            <button
                type="button"
                onClick={() => onSelect(article.slug)}
                className={cn(
                    'flex w-full items-center justify-between gap-3 rounded-xl bg-card text-left shadow-md',
                    'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg motion-reduce:transition-none motion-reduce:hover:translate-y-0',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    compact ? 'px-3.5 py-2.5' : 'px-4 py-4',
                )}
            >
                <h3
                    className={cn(
                        'min-w-0 flex-1 truncate font-semibold text-foreground',
                        compact ? 'text-sm' : 'text-base',
                    )}
                >
                    {article.title}
                </h3>
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
            </button>
        </li>
    )
}

function GroupSection({
    group,
    defaultOpen,
    onSelect,
}: {
    group: CategoryGroup
    defaultOpen: boolean
    onSelect: (slug: string) => void
}) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(defaultOpen)
    const label = t(`help.categories.${group.category}`, { defaultValue: group.category })
    // Icône de catégorie monochrome (balayage visuel des en-têtes de groupe, §1.2).
    const CategoryIcon = helpCategoryIcon(group.category)
    return (
        <div className="overflow-hidden rounded-xl border border-border bg-card/50">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    {open ? (
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <CategoryIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    {label}
                </span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {group.articles.length}
                </span>
            </button>
            {open && (
                <ul className="space-y-2 p-2 pt-0">
                    {group.articles.map((a) => (
                        <ArticleRow key={a.id} article={a} compact onSelect={onSelect} />
                    ))}
                </ul>
            )}
        </div>
    )
}

export function HelpContextualArticles({
    articles,
    onSelectArticle,
}: {
    articles: HelpArticle[]
    onSelectArticle: (slug: string) => void
}) {
    const count = articles.length

    // ≥10 → sections repliables par catégorie.
    const grouped = useMemo(
        () => (count >= GROUPING_THRESHOLD ? buildGroups(articles) : null),
        [articles, count],
    )

    if (grouped && grouped.length >= 2) {
        // Ouvre la section de la catégorie du 1er article (l'article principal de la page).
        const contextCategory = articles[0]?.category || UNCATEGORIZED
        return (
            <div className="space-y-2">
                {grouped.map((group, i) => (
                    <GroupSection
                        key={group.category}
                        group={group}
                        defaultOpen={group.category === contextCategory || (i === 0 && contextCategory === UNCATEGORIZED)}
                        onSelect={onSelectArticle}
                    />
                ))}
            </div>
        )
    }

    // 2–5 → liste plate ; 6–9 → liste compacte (et repli si le regroupement
    // n'a pas produit ≥2 groupes).
    const compact = count >= COMPACT_LIST_MIN
    return (
        <ul className="space-y-2.5">
            {articles.map((a) => (
                <ArticleRow key={a.id} article={a} compact={compact} onSelect={onSelectArticle} />
            ))}
        </ul>
    )
}
